import { useEffect, useState, useRef, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { BellRing, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const SERVICE_ROLES = ['waiter', 'manager', 'director', 'shisha_expert', 'bartender'];
const POLL_INTERVAL_MS = 5000;

/**
 * Mounted at the app layout level for all authenticated staff.
 * - Polls /api/service-requests/active every 5s.
 * - If there are pending (unaccepted) requests, plays a repeating beep via Web Audio
 *   and shows a floating red banner.
 * - First staff clicks Accept → request flips to accepted, next poll removes it
 *   from the pending list → beep stops everywhere.
 *
 * Audio uses a synthesised beep (no external asset needed). Browser autoplay
 * policies require a user gesture before audio can play — we unlock the
 * AudioContext on the first user click anywhere on the page.
 */
export default function ServiceRequestListener() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [accepting, setAccepting] = useState(null);
  const audioCtxRef = useRef(null);
  const beepTimerRef = useRef(null);
  const unlockedRef = useRef(false);
  const shouldBeep = user && SERVICE_ROLES.includes(user.role);

  // Unlock the AudioContext on first user gesture
  useEffect(() => {
    if (!shouldBeep) return undefined;
    const unlock = () => {
      if (unlockedRef.current) return;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          audioCtxRef.current = new Ctx();
          unlockedRef.current = true;
        }
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('click', unlock, { once: true, capture: true });
    window.addEventListener('keydown', unlock, { once: true, capture: true });
    window.addEventListener('touchstart', unlock, { once: true, capture: true });
    return () => {
      window.removeEventListener('click', unlock, true);
      window.removeEventListener('keydown', unlock, true);
      window.removeEventListener('touchstart', unlock, true);
    };
  }, [shouldBeep]);

  const playBeep = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    // Two short beeps at different pitches — attention-grabbing without being harsh
    const now = ctx.currentTime;
    [0, 0.18].forEach((offset, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = idx === 0 ? 880 : 660;
      gain.gain.setValueAtTime(0, now + offset);
      gain.gain.linearRampToValueAtTime(0.35, now + offset + 0.02);
      gain.gain.linearRampToValueAtTime(0, now + offset + 0.14);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + offset);
      osc.stop(now + offset + 0.15);
    });
  }, []);

  const fetchPending = useCallback(async () => {
    if (!shouldBeep) return;
    try {
      const { data } = await api.get('/service-requests/active');
      setPending(Array.isArray(data) ? data : []);
    } catch {
      // silent — preserves beep if network blips
    }
  }, [shouldBeep]);

  // Poll every 5s
  useEffect(() => {
    if (!shouldBeep) return undefined;
    fetchPending();
    const id = setInterval(fetchPending, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [shouldBeep, fetchPending]);

  // Start / stop the beep based on pending count
  useEffect(() => {
    if (!shouldBeep) return undefined;
    if (pending.length === 0) {
      if (beepTimerRef.current) clearInterval(beepTimerRef.current);
      beepTimerRef.current = null;
      return undefined;
    }
    // Play immediately, then every 2s while unaccepted
    playBeep();
    if (beepTimerRef.current) clearInterval(beepTimerRef.current);
    beepTimerRef.current = setInterval(playBeep, 2000);
    return () => {
      if (beepTimerRef.current) clearInterval(beepTimerRef.current);
      beepTimerRef.current = null;
    };
  }, [pending.length, shouldBeep, playBeep]);

  const accept = async (req) => {
    setAccepting(req.id);
    try {
      await api.put(`/service-requests/${req.id}/accept`);
      toast.success(`Accepted Table ${req.table_number}`);
      fetchPending();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info('Already accepted by another staff');
      } else {
        toast.error(err.response?.data?.detail || 'Could not accept');
      }
      fetchPending();
    } finally {
      setAccepting(null);
    }
  };

  if (!shouldBeep || pending.length === 0) return null;

  return (
    <div
      className="fixed top-3 right-3 z-[60] max-w-xs w-[calc(100vw-1.5rem)] sm:w-80 bg-red-500/15 border border-red-500/40 rounded-sm shadow-xl backdrop-blur-md overflow-hidden animate-pulse-slow"
      data-testid="service-request-banner"
      role="alert"
    >
      <div className="flex items-center gap-2 px-3 py-2 bg-red-500/20 border-b border-red-500/30">
        <BellRing className="w-4 h-4 text-red-300 animate-pulse" />
        <p className="text-red-100 text-xs font-sans uppercase tracking-wider font-bold">
          Service Call{pending.length > 1 ? `s (${pending.length})` : ''}
        </p>
      </div>
      <div className="divide-y divide-white/5 max-h-72 overflow-y-auto">
        {pending.map(req => {
          // Friendly labels for the bell tray. `payment_eftpos` is the new
          // EFTPOS-at-table summons; `payment` covers the older cash-at-counter
          // path. Everything else falls back to the raw type string.
          const TYPE_LABELS = {
            payment_eftpos: 'Time to Collect Payment via EFTPOS',
            payment: 'Payment assistance',
            help: 'Service help',
            water: 'Refill water',
            bill: 'Bring the bill',
            napkins: 'Need napkins',
            coal: 'Change shisha coal',
          };
          const friendly = TYPE_LABELS[req.request_type] || req.request_type;
          const isEftpos = req.request_type === 'payment_eftpos';
          return (
          <div key={req.id} className={`p-3 flex items-center gap-3 ${isEftpos ? 'bg-blue-accent/[0.06] border-l-2 border-blue-accent' : ''}`} data-testid={`service-request-${req.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-sans font-medium">Table {req.table_number}</p>
              <p className={`text-xs font-sans ${isEftpos ? 'text-blue-accent font-semibold' : 'text-zinc-400 capitalize'}`}>{friendly}{req.customer_name ? ` • ${req.customer_name}` : ''}</p>
              {req.note && <p className="text-zinc-500 text-[11px] font-sans mt-0.5 italic">"{req.note}"</p>}
            </div>
            <Button
              size="sm"
              onClick={() => accept(req)}
              disabled={accepting === req.id}
              className="h-8 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 rounded-sm font-sans text-xs"
              data-testid={`accept-service-${req.id}`}
            >
              {accepting === req.id ? <X className="w-3 h-3" /> : <><Check className="w-3 h-3 mr-1" /> Accept</>}
            </Button>
          </div>
          );
        })}
      </div>
    </div>
  );
}
